import React, { useState, useEffect } from 'react';

interface Step {
  label: string;
  key: string;
}

const DEFAULT_STEPS: Step[] = [
  { label: 'Reading session details', key: 'reading' },
  { label: 'Drafting note', key: 'drafting' },
  { label: 'Formatting', key: 'formatting' },
];

/**
 * Multi-step progress indicator for AI note generation.
 * Transforms waiting from anxious to exciting by showing
 * a visual step-through of the generation process.
 *
 * Can be driven externally via `currentStep` or will auto-advance
 * on a timer when `autoAdvance` is true.
 */
export default function AIGenerationProgress({
  steps = DEFAULT_STEPS,
  currentStep,
  autoAdvance = true,
  autoAdvanceInterval = 3000,
  message = 'Writing your note...',
}: {
  steps?: Step[];
  currentStep?: string;
  autoAdvance?: boolean;
  autoAdvanceInterval?: number;
  message?: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-advance through steps when not externally controlled
  useEffect(() => {
    if (!autoAdvance || currentStep) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => {
        // Stay on last step (don't loop)
        if (prev >= steps.length - 1) return prev;
        return prev + 1;
      });
    }, autoAdvanceInterval);
    return () => clearInterval(timer);
  }, [autoAdvance, autoAdvanceInterval, currentStep, steps.length]);

  // Sync with external currentStep
  useEffect(() => {
    if (!currentStep) return;
    const idx = steps.findIndex((s) => s.key === currentStep);
    if (idx >= 0) setActiveIndex(idx);
  }, [currentStep, steps]);

  return (
    <div className="flex flex-col items-center gap-4 py-6 px-4">
      {/* Animated writing icon */}
      <div className="relative">
        <div className="w-10 h-10 rounded-full bg-kindredly-blue/10 dark:bg-kindredly-blue/20 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-kindredly-blue animate-bounce-gentle"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>
      </div>

      {/* Contextual message */}
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {message}
      </p>

      {/* Step indicators */}
      <div className="flex items-center gap-2 text-xs">
        {steps.map((step, index) => {
          const isActive = index === activeIndex;
          const isComplete = index < activeIndex;

          return (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <span
                  className={`transition-colors duration-500 ${
                    isComplete
                      ? 'text-kindredly-green'
                      : 'text-gray-300 dark:text-gray-600'
                  }`}
                >
                  &rarr;
                </span>
              )}
              <span
                className={`transition-all duration-500 px-2 py-1 rounded-full whitespace-nowrap ${
                  isActive
                    ? 'bg-kindredly-blue/10 text-kindredly-blue font-semibold dark:bg-kindredly-blue/20'
                    : isComplete
                      ? 'text-kindredly-green font-medium'
                      : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {isComplete && (
                  <svg
                    className="w-3 h-3 inline-block mr-0.5 -mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                {step.label}
                {isActive && (
                  <span className="inline-block ml-0.5 animate-ellipsis" />
                )}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
